import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { all_routes } from "../../Router/all_routes";
import { getPosState, posError } from "../../services/PosApiServices";
import { fetchBranchSettings } from "../../redux/settingbyBranch";
import PosReturn from "../../components/transactions/pos/module/PosReturn";
import PosShiftClose from "../../components/transactions/pos/module/PosShiftClose";
import PosDayClose from "../../components/transactions/pos/module/PosDayClose";
import PosReprint from "../../components/transactions/pos/module/PosReprint";
import {
  PosBillSummary,
  PosDayCloseReport,
  PosShiftCloseReport,
  PosSummaryReport,
} from "../../components/transactions/pos/module/PosReports";
import "../../style/css/pos-module.css";

/**
 * Transactions ▸ POS. The till itself is /Transactions/POS/Index (POS.jsx);
 * every other POS screen is a slug under the same path. Each screen is its own
 * item of the sidebar's POS group (DB script section 29), with its own rights,
 * looked up by the item's name.
 */
const SCREENS = {
  return: { menu: "POS Sales Return", kind: "page" },
  shiftclose: { menu: "POS Shift Close", kind: "page" },
  dayclose: { menu: "POS Day Close", kind: "page" },
  reprint: { menu: "POS Invoice Re-Print", kind: "page" },
  shiftclosereport: { menu: "POS Shift Close Report", kind: "report" },
  dayclosereport: { menu: "POS Day Close Report", kind: "report" },
  summaryreport: { menu: "POS Summary Report", kind: "report" },
  billsummary: { menu: "POS Bill Summary", kind: "report" },
};

const POS_MENU_NAMES = ["POS Invoice", ...Object.values(SCREENS).map((s) => s.menu)];

const PosModule = () => {
  const { screen: slug = "" } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { branch } = useSelector((state) => state.branchlist);
  const { userId } = useSelector((state) => state.userauth);
  const { menuItems } = useSelector((state) => state.amc);

  const key = slug.toLowerCase();
  const screen = SCREENS[key];

  // The screen's own menu row decides; a database without the POS menu rows
  // yet keeps full access, so the screens still work.
  const permit = useMemo(() => {
    const all = menuItems?.menuPermissions || [];
    const posRows = all.filter?.((e) => POS_MENU_NAMES.includes(e.menu?.name)) || [];
    if (!posRows.length) return { view: true, add: true, edit: true, deletion: true };
    const row = posRows.find((e) => e.menu?.name === screen?.menu);
    return {
      view: Boolean(row?.view),
      add: Boolean(row?.add),
      edit: Boolean(row?.edit),
      deletion: Boolean(row?.deletion),
    };
  }, [menuItems, screen]);

  const [state, setState] = useState(null);
  const needsState = screen?.kind === "page";

  const reload = useCallback(async () => {
    if (!branch?.guid) return;
    try {
      setState(await getPosState(branch.guid));
    } catch (error) {
      toast.error(posError(error, "Could not read the POS business date"));
    }
  }, [branch]);

  useEffect(() => {
    if (needsState) reload();
  }, [needsState, reload]);

  useEffect(() => {
    if (branch?.guid) dispatch(fetchBranchSettings(branch.guid));
  }, [dispatch, branch]);

  if (!screen) {
    return (
      <div className="page-wrapper">
        <div className="content pm">
          <div className="pm-card">That POS screen does not exist.</div>
        </div>
      </div>
    );
  }

  if (!permit.view) {
    return (
      <div className="page-wrapper">
        <div className="content pm">
          <div className="pm-card">You do not have the right to open {screen.menu}.</div>
        </div>
      </div>
    );
  }

  const props = { branch, userId, state, reload, permit };

  switch (key) {
    case "shiftclosereport":
      return <PosShiftCloseReport branch={branch} />;
    case "dayclosereport":
      return <PosDayCloseReport branch={branch} />;
    case "summaryreport":
      return <PosSummaryReport branch={branch} />;
    case "billsummary":
      return <PosBillSummary branch={branch} />;
    default:
      break;
  }

  let body;
  if (key === "return") body = <PosReturn {...props} />;
  else if (key === "shiftclose") body = <PosShiftClose {...props} />;
  else if (key === "dayclose")
    body = <PosDayClose {...props} onShiftClose={() => navigate(`${all_routes.POSModule}/ShiftClose`)} />;
  else body = <PosReprint {...props} />;

  return (
    <div className="page-wrapper">
      <div className="content pm">{body}</div>
    </div>
  );
};

export default PosModule;
